import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAmazonProfile } from '../../contexts/AmazonProfileContext';
import { useTheme } from '../../contexts/ThemeContext';
import StoreHubSidebar from '../stores/StoreHubSidebar';
import StoreHubAdminChrome from './StoreHubAdminChrome';
import { qcommerceApi } from '../../services/qcommerceApi';
import { isQuickCommerceHubFilter } from '../../constants/qcommerce';
import { normalizeStoreList } from '../../utils/storeUtils';

const HUB_PLATFORM_KEY = 'sellerclub.hubPlatform';

const StoreHubChromeContext = createContext({
  setQcStoreCount: () => {},
  qcStoreCount: null,
});

export function useStoreHubChrome() {
  return useContext(StoreHubChromeContext);
}

function readPersistedHubPlatform() {
  try {
    const v = sessionStorage.getItem(HUB_PLATFORM_KEY);
    return v === 'quick_commerce' ? 'quick_commerce' : 'amazon';
  } catch {
    return 'amazon';
  }
}

/** Store hub + administration pages — no workspace (Dashboard/Campaigns) sidebar. */
export default function StoreHubShell() {
  const { user, logout } = useAuth();
  const { profiles } = useAmazonProfile();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [qcStoreCount, setQcStoreCount] = useState(null);

  const stores = useMemo(() => normalizeStoreList(profiles), [profiles]);
  const amazonTotalCount = stores.length;

  const hubPlatform = useMemo(() => {
    if (location.pathname.startsWith('/stores')) {
      const p = searchParams.get('platform');
      return isQuickCommerceHubFilter(p) ? 'quick_commerce' : 'amazon';
    }
    if (location.pathname === '/user-management') {
      return searchParams.get('tab') === 'quick_commerce' ? 'quick_commerce' : 'amazon';
    }
    if (location.pathname.startsWith('/admin/qc-portal-accounts')) {
      return 'quick_commerce';
    }
    return readPersistedHubPlatform();
  }, [location.pathname, searchParams]);

  useEffect(() => {
    try {
      sessionStorage.setItem(HUB_PLATFORM_KEY, hubPlatform);
    } catch {
      /* ignore */
    }
  }, [hubPlatform]);

  useEffect(() => {
    if (!user) return;
    qcommerceApi
      .listHubStores()
      .then((res) => {
        if (res.data?.success) setQcStoreCount(res.data.count ?? res.data.stores?.length ?? 0);
      })
      .catch(() => setQcStoreCount(0));
  }, [user]);

  const qcHubActive = hubPlatform === 'quick_commerce';

  const handlePlatformFilter = useCallback(
    (next) => {
      const qc = next === 'quick_commerce';
      try {
        sessionStorage.setItem(HUB_PLATFORM_KEY, qc ? 'quick_commerce' : 'amazon');
      } catch {
        /* ignore */
      }
      navigate(qc ? '/stores?platform=quick_commerce' : '/stores?platform=amazon');
    },
    [navigate]
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const chromeValue = useMemo(() => ({ setQcStoreCount, qcStoreCount }), [qcStoreCount]);

  return (
    <StoreHubChromeContext.Provider value={chromeValue}>
      <div className={`h-screen flex overflow-hidden ${isDarkMode ? 'bg-slate-950' : 'bg-slate-100'}`}>
        <StoreHubSidebar
          user={user}
          platformFilter={hubPlatform}
          qcHubActive={qcHubActive}
          qcStoreCount={qcStoreCount}
          amazonTotalCount={amazonTotalCount}
          onPlatformFilter={handlePlatformFilter}
          onLogout={handleLogout}
          isDarkMode={isDarkMode}
        />
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          {!location.pathname.startsWith('/stores') ? (
            <div
              className={`shrink-0 flex items-center justify-end gap-2 px-6 py-2 border-b ${
                isDarkMode ? 'border-slate-800/90 bg-slate-900/90' : 'border-slate-200 bg-white'
              }`}
            >
              <button
                type="button"
                onClick={toggleTheme}
                className={`p-2 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'border-slate-700 bg-slate-800 text-slate-300'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
                title="Toggle theme"
              >
                {isDarkMode ? '☀️' : '🌙'}
              </button>
            </div>
          ) : null}
          <div className="flex-1 overflow-y-auto min-h-0">
            {location.pathname.startsWith('/stores') ? (
              <Outlet />
            ) : (
              <StoreHubAdminChrome>
                <Outlet />
              </StoreHubAdminChrome>
            )}
          </div>
        </div>
      </div>
    </StoreHubChromeContext.Provider>
  );
}
